import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const ease = [0.76, 0, 0.24, 1];

const RevealLine = ({ children, delay = 0 }) => (
  <span className="reveal-mask">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease, delay }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen bg-navy text-white overflow-hidden" data-testid="hero-section">
      {/* Grid lines */}
      <div className="absolute inset-0 pointer-events-none opacity-20"
           style={{ backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px)", backgroundSize: "8.333% 100%" }} />

      {/* Top meta row */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="absolute top-24 left-0 right-0 z-10"
      >
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 flex items-center justify-end text-[10px] tracking-[0.25em] uppercase font-semibold text-white/60" />
      </motion.div>

      {/* Kinetic headline */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12 pt-40 md:pt-52 lg:pt-56"
      >
        <div className="mb-6 md:mb-8 reveal-mask">
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, ease, delay: 0.3 }}
            className="inline-flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase font-semibold text-white/70"
            data-testid="hero-eyebrow"
          >
            Junk Removal · Demolition · Cleanup
          </motion.div>
        </div>

        <h1 className="font-display font-black uppercase leading-[0.9] tracking-[-0.03em] text-[10vw] md:text-[7.5vw] lg:text-[6vw]"
            data-testid="hero-headline">
          <RevealLine delay={0.5}>We haul the weight</RevealLine>
          <RevealLine delay={0.62}>off your property,</RevealLine>
          <RevealLine delay={0.74}>
            <span className="italic font-medium tracking-[-0.04em] text-white/95">AND</span> the weight
          </RevealLine>
          <RevealLine delay={0.86}>off your shoulders.</RevealLine>
        </h1>

        <div className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 1.1 }}
            className="max-w-md text-base md:text-lg leading-relaxed text-white/80"
            data-testid="hero-description"
          >
            Serving Central Alabama with dependable junk removal, demolition, and property cleanup. Whether it&apos;s one item or an entire home, Peak Disposal delivers fast, professional service from start to finish.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 1.25 }}
            className="flex items-center gap-4"
          />
        </div>
      </motion.div>
    </section>
  );
}
